import { Download, Upload } from 'lucide-react';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { Combobox } from '@components/combobox';
import { Filters } from '@components/filters';
import { PageHeader } from '@components/page-header';
import { Spinner } from '@components/spinner';
import {
    CellProps,
    ReactColumn,
    SortingRule,
    TableComponent
} from '@components/table';
import { Button } from '@components/ui/button';
import { Switch } from '@components/ui/switch';
import { toast } from '@core/toast';
import { WorkoutTypeV2 } from '../../core/api/FitnessActivityClient';
import { useWorkoutTypeMappingStore } from './list-store';
import { Api } from '../../core/api';
import { injectTSDI } from '../../core/tsdi';
import { useError } from '../../components/hooks/useError';
import { ImportCsvModal } from './import-csv-modal';

const RejectedCell = ({
    mapping,
    onSaved
}: {
    mapping: WorkoutTypeMappingDto;
    onSaved: () => void;
}) => {
    const { fitnessActivityClient } = injectTSDI(Api);
    const showError = useError();
    const [rejected, setRejected] = useState(mapping.rejected);
    const [saving, setSaving] = useState(false);

    React.useEffect(() => {
        setRejected(mapping.rejected);
    }, [mapping.rejected]);

    const handleChange = async () => {
        const newValue = !rejected;
        setRejected(newValue);
        setSaving(true);
        try {
            await fitnessActivityClient.admin.workoutTypeMappingAdminControllerUpdate(
                {
                    rawType: mapping.rawType,
                    workoutType: mapping.workoutType ?? undefined,
                    rejected: newValue
                }
            );
            toast.success(`Updated "${mapping.rawType}"`);
            onSaved();
        } catch (error) {
            setRejected(!newValue);
            showError(error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex items-center gap-2">
            <Switch
                size="sm"
                checked={rejected}
                onCheckedChange={handleChange}
                disabled={saving}
                className="data-[state=checked]:bg-destructive"
            />
            {saving && <Spinner size="sm" />}
        </div>
    );
};

interface WorkoutTypeMappingDto {
    rawType: string;
    workoutType?: WorkoutTypeV2 | null;
    rejected: boolean;
}

interface WorkoutTypeOption {
    value: string;
    label: string;
}

const UNMAPPED_VALUE = '__UNMAPPED__';

const WORKOUT_TYPE_CATEGORIES: Record<string, WorkoutTypeV2[]> = {
    Foot: [
        WorkoutTypeV2.RUNNING,
        WorkoutTypeV2.WALKING,
        WorkoutTypeV2.TREKKING,
        WorkoutTypeV2.HIKING,
        WorkoutTypeV2.TREADMILL,
        WorkoutTypeV2.NORDIC_WALKING
    ],
    Wheels: [
        WorkoutTypeV2.CYCLING,
        WorkoutTypeV2.ELECTRIC_CYCLING,
        WorkoutTypeV2.INDOOR_CYCLING,
        WorkoutTypeV2.ROLLERBLADE,
        WorkoutTypeV2.SKATEBOARD,
        WorkoutTypeV2.SCOOTER,
        WorkoutTypeV2.WHEELCHAIR
    ],
    Winter: [
        WorkoutTypeV2.SKIING,
        WorkoutTypeV2.SNOWBOARDING,
        WorkoutTypeV2.SKATING,
        WorkoutTypeV2.SKI_TOURING,
        WorkoutTypeV2.CROSS_COUNTRY_SKIING,
        WorkoutTypeV2.BIATHLON
    ],
    Water: [
        WorkoutTypeV2.SWIMMING,
        WorkoutTypeV2.ROWING,
        WorkoutTypeV2.SURFING,
        WorkoutTypeV2.SAILING,
        WorkoutTypeV2.STAND_UP_PADDLE,
        WorkoutTypeV2.AQUA_GYM
    ],
    'Team Sports': [
        WorkoutTypeV2.FOOTBALL,
        WorkoutTypeV2.VOLLEYBALL,
        WorkoutTypeV2.BASKETBALL,
        WorkoutTypeV2.HANDBALL
    ],
    'Stationary / Indoor': [
        WorkoutTypeV2.TRAINING,
        WorkoutTypeV2.DANCING,
        WorkoutTypeV2.MARTIAL_ARTS,
        WorkoutTypeV2.TENNIS,
        WorkoutTypeV2.SQUASH,
        WorkoutTypeV2.PADEL,
        WorkoutTypeV2.TABLE_TENNIS,
        WorkoutTypeV2.BADMINTON,
        WorkoutTypeV2.CLIMBING,
        WorkoutTypeV2.INDOOR_ROWING_TIME,
        WorkoutTypeV2.ELLIPTICAL,
        WorkoutTypeV2.STRENGTH,
        WorkoutTypeV2.HIIT,
        WorkoutTypeV2.CARDIO,
        WorkoutTypeV2.CROSSFIT,
        WorkoutTypeV2.YOGA,
        WorkoutTypeV2.PILATES,
        WorkoutTypeV2.STRETCHING,
        WorkoutTypeV2.AEROBIC,
        WorkoutTypeV2.ZUMBA
    ],
    Other: [WorkoutTypeV2.GOLF, WorkoutTypeV2.HORSE_RIDING, WorkoutTypeV2.OTHER]
};

const formatWorkoutTypeLabel = (workoutType: string) =>
    workoutType.replace(/_/g, ' ');

const WORKOUT_TYPE_OPTIONS: WorkoutTypeOption[] = [
    {
        value: UNMAPPED_VALUE,
        label: 'Unmapped (None)'
    },
    ...[
        ...Object.entries(WORKOUT_TYPE_CATEGORIES).flatMap(
            ([, types]) => types
        ),
        ...Object.values(WorkoutTypeV2).filter(
            (type) =>
                !Object.values(WORKOUT_TYPE_CATEGORIES).flat().includes(type)
        )
    ].map((type) => ({
        value: type,
        label: formatWorkoutTypeLabel(type)
    }))
];

const WORKOUT_TYPE_FILTER_OPTIONS = [
    { label: 'All', value: '' },
    { label: 'Unmapped', value: 'UNMAPPED' },
    ...Object.values(WorkoutTypeV2).map((type) => ({
        label: formatWorkoutTypeLabel(type),
        value: type
    }))
];

export const WorkoutTypeMappings = observer(() => {
    const { fitnessActivityClient } = injectTSDI(Api);
    const showError = useError();
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [editedWorkoutTypes, setEditedWorkoutTypes] = useState<
        Record<string, string>
    >({});
    const [savingRows, setSavingRows] = useState<Record<string, boolean>>({});
    const {
        workoutTypeMappings,
        searchParams,
        sorting,
        setSorting,
        setSearchTerm,
        dispatch,
        workoutTypeFilter,
        setWorkoutTypeFilter,
        rejectedFilter,
        setRejectedFilter
    } = useWorkoutTypeMappingStore();
    const searchParamsRef = React.useRef(searchParams);
    const workoutTypeFilterRef = React.useRef(workoutTypeFilter);
    const rejectedFilterRef = React.useRef(rejectedFilter);

    React.useEffect(() => {
        searchParamsRef.current = searchParams;
    }, [searchParams]);

    React.useEffect(() => {
        workoutTypeFilterRef.current = workoutTypeFilter;
    }, [workoutTypeFilter]);

    React.useEffect(() => {
        rejectedFilterRef.current = rejectedFilter;
    }, [rejectedFilter]);

    React.useEffect(() => {
        setEditedWorkoutTypes((previousEditedWorkoutTypes) => {
            if (!Object.keys(previousEditedWorkoutTypes).length) {
                return previousEditedWorkoutTypes;
            }

            const rowWorkoutTypes = new Map(
                (workoutTypeMappings.$.content || []).map((mapping) => [
                    mapping.rawType,
                    mapping.workoutType || UNMAPPED_VALUE
                ])
            );

            let hasChanges = false;
            const nextEditedWorkoutTypes = { ...previousEditedWorkoutTypes };
            Object.entries(previousEditedWorkoutTypes).forEach(
                ([rawType, selectedWorkoutType]) => {
                    const currentWorkoutType = rowWorkoutTypes.get(rawType);
                    if (
                        !currentWorkoutType ||
                        currentWorkoutType === selectedWorkoutType
                    ) {
                        delete nextEditedWorkoutTypes[rawType];
                        hasChanges = true;
                    }
                }
            );

            return hasChanges
                ? nextEditedWorkoutTypes
                : previousEditedWorkoutTypes;
        });
    }, [workoutTypeMappings.$.content]);

    const onExport = async () => {
        try {
            const response =
                await fitnessActivityClient.admin.workoutTypeMappingAdminControllerExport();
            const blob = await response.blob();
            const data = new Blob([blob], { type: 'text/csv' });
            const csvURL = window.URL.createObjectURL(data);
            const tempLink = document.createElement('a');
            tempLink.href = csvURL;
            tempLink.setAttribute('download', 'workout-type-mappings.csv');
            tempLink.click();
        } catch (e) {
            showError(e);
        }
    };

    const onSortingChange = (sorting?: SortingRule) => {
        if (sorting) {
            setSorting(sorting);
        }
    };

    const reloadMappings = React.useCallback(() => {
        workoutTypeMappings.request([
            searchParamsRef.current,
            workoutTypeFilterRef.current,
            rejectedFilterRef.current
        ]);
    }, [workoutTypeMappings]);

    const handleWorkoutTypeChange = React.useCallback(
        async (mapping: WorkoutTypeMappingDto, selectedWorkoutType: string) => {
            const currentWorkoutType =
                editedWorkoutTypes[mapping.rawType] ||
                mapping.workoutType ||
                UNMAPPED_VALUE;

            if (selectedWorkoutType === currentWorkoutType) {
                return;
            }

            setEditedWorkoutTypes((previousEditedWorkoutTypes) => ({
                ...previousEditedWorkoutTypes,
                [mapping.rawType]: selectedWorkoutType
            }));
            setSavingRows((previousSavingRows) => ({
                ...previousSavingRows,
                [mapping.rawType]: true
            }));

            try {
                await fitnessActivityClient.admin.workoutTypeMappingAdminControllerUpdate(
                    {
                        rawType: mapping.rawType,
                        workoutType:
                            selectedWorkoutType === UNMAPPED_VALUE
                                ? undefined
                                : (selectedWorkoutType as WorkoutTypeV2),
                        rejected: mapping.rejected
                    }
                );
                toast.success(`Updated "${mapping.rawType}"`);
                reloadMappings();
            } catch (error) {
                setEditedWorkoutTypes((previousEditedWorkoutTypes) => ({
                    ...previousEditedWorkoutTypes,
                    [mapping.rawType]: currentWorkoutType
                }));
                showError(error);
            } finally {
                setSavingRows((previousSavingRows) => {
                    const remainingRows = { ...previousSavingRows };
                    delete remainingRows[mapping.rawType];
                    return remainingRows;
                });
            }
        },
        [editedWorkoutTypes, fitnessActivityClient, showError, reloadMappings]
    );

    const columns: ReactColumn<WorkoutTypeMappingDto>[] = [
        {
            Header: 'Raw Type',
            accessor: 'rawType',
            Cell: ({ row: { original } }: CellProps<WorkoutTypeMappingDto>) => (
                <span className="font-mono text-sm">{original.rawType}</span>
            )
        },
        {
            Header: 'Workout Type',
            accessor: 'workoutType',
            style: {
                minWidth: 320
            },
            Cell: ({ row: { original } }: CellProps<WorkoutTypeMappingDto>) => {
                const selectedWorkoutType =
                    editedWorkoutTypes[original.rawType] ??
                    original.workoutType ??
                    UNMAPPED_VALUE;
                const isSaving = Boolean(savingRows[original.rawType]);
                const selectedWorkoutTypeOption =
                    WORKOUT_TYPE_OPTIONS.find(
                        ({ value }) => value === selectedWorkoutType
                    ) || WORKOUT_TYPE_OPTIONS[0];

                return (
                    <div className="flex items-center gap-3">
                        <Combobox<WorkoutTypeOption>
                            size="small"
                            options={WORKOUT_TYPE_OPTIONS}
                            value={selectedWorkoutTypeOption}
                            getOptionLabel={({ label }) => label}
                            getOptionKey={({ value }) => value}
                            searchPlaceholder="Search workout type..."
                            emptyText="No workout type found"
                            onChange={(option) =>
                                option &&
                                void handleWorkoutTypeChange(
                                    original,
                                    option.value
                                )
                            }
                            disabled={isSaving}
                        />
                        {isSaving && <Spinner size="sm" />}
                    </div>
                );
            }
        },
        {
            Header: 'Rejected',
            accessor: 'rejected',
            style: { width: 100 },
            Cell: ({ row: { original } }: CellProps<WorkoutTypeMappingDto>) => (
                <RejectedCell mapping={original} onSaved={reloadMappings} />
            )
        }
    ];

    return (
        <>
            <PageHeader
                title="Workout Type Mappings"
                actions={
                    <>
                        <Button variant="outline" size="sm" onClick={onExport}>
                            <Download />
                            Export CSV
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setImportModalOpen(true)}
                        >
                            <Upload />
                            Import CSV
                        </Button>
                    </>
                }
            />

            <div className="mb-4 flex flex-wrap items-end gap-3">
                <Filters<string, false>
                    label="Workout Type"
                    value={workoutTypeFilter}
                    options={WORKOUT_TYPE_FILTER_OPTIONS}
                    onChange={(val) => setWorkoutTypeFilter(val)}
                    width={250}
                />
                <Filters<string, false>
                    label="Rejected"
                    value={
                        rejectedFilter === undefined
                            ? ''
                            : String(rejectedFilter)
                    }
                    options={[
                        { label: 'All', value: '' },
                        { label: 'Rejected', value: 'true' },
                        { label: 'Not Rejected', value: 'false' }
                    ]}
                    onChange={(val) =>
                        setRejectedFilter(
                            val === '' ? undefined : val === 'true'
                        )
                    }
                    width={180}
                />
            </div>

            <TableComponent
                columns={columns}
                items={workoutTypeMappings}
                onSortingChange={onSortingChange}
                sorting={sorting as SortingRule | undefined}
                onSearchChange={setSearchTerm}
                searchPlaceholder="Search by raw type..."
                onChangePage={(page) => dispatch({ type: 'page', page })}
                onPageSizeChange={(size) => dispatch({ type: 'size', size })}
            />

            <ImportCsvModal
                open={importModalOpen}
                onClose={() => setImportModalOpen(false)}
                onSuccess={reloadMappings}
            />
        </>
    );
});
